import { ref, reactive } from 'vue'
import * as math from 'mathjs'
import { matrix } from 'mathjs'

export const PI = Math.PI;

// Modify math.js matrix to add some helper functions
export let MatrixPrototype = Object.getPrototypeOf(matrix([1, 2, 3]));
MatrixPrototype.toValString = function() {
  return `[${this.size()[0]}x${this.size()[1]}]`;
};
MatrixPrototype.toDenseString = function() {
  let str = ``;
  for (let i = 0; i < this.size()[0]; ++i) {
    for (let j = 0; j < this.size()[1]; ++j) {
      str += `${this.get([i, j])} `;
    }
    str += '\n';
  }
  return str;
};

// Add SerUtil support
MatrixPrototype.serInfo = {
  type: 'Matrix',
  ctor: () => {
    return matrix();
  },
}
MatrixPrototype.writeToJson = function() {
  return {
    _type: 'Matrix',
    data: this.toArray(),
  }
}
MatrixPrototype.readFromJson = function(jsonObj) {
  // Set to the array values, in place
  this.resize([jsonObj.data.length, jsonObj.data[0].length]);
  for (let i = 0; i < jsonObj.data.length; i++) {
    for (let j = 0; j < jsonObj.data[0].length; j++) {
      this.set([i, j], jsonObj.data[i][j]);
    }
  }
}

export function cos(val) {
  return Math.cos(val)
}

export function sin(val) {
  return Math.sin(val)
}

export function toRads(degs) {
  return 2*PI*degs/360.0;
}

export function toDegs(rads) {
  return 360.0*rads/(2*PI);
}

export function arcsin(val) {
  return Math.asin(val)
}

export function arccos(val) {
  return Math.acos(val)
}

// Result in radians, [-pi/2, pi/2]
export function arctan(val) {
  return Math.atan(val)
}

export function sqrt(val) {
  return Math.sqrt(val);
}

export function ln(val) {
  return Math.log(val);
}

export function log(val) {
  return Math.log10(val);
}

export function exp(val) {
  return Math.exp(val);
}

export function clampToMax(val, max) {
  return Math.min(val, max);
}

export function clampToMin(val, min) {
  return Math.max(val, min);
}

export class MatrixUtils {
  static getRow(m, rowIndex) {
    return math.row(m, rowIndex).toArray()[0]
  }

  static setRow(m, rowIndex, newRowArr) {
    for (let i = 0; i < newRowArr.length; i++) {
      m.set([rowIndex, i], newRowArr[i]);
    }
  }

  static setCol(m, colIndex, newColArr) {
    for (let i = 0; i < newColArr.length; i++) {
      m.set([i, colIndex], newColArr[i]);
    }
  }

  static sumOfMatrices(matrixArr) {
    let sum = math.zeros(matrixArr[0].size())
    for (let m of matrixArr) {
      sum = math.add(sum, m)
    }
    return sum
  }

  static sumOfMatrixMaxs(matrixArr) {
    let sum = 0
    for (let m of matrixArr) {
      sum += math.max(m)
    }
    return sum
  }

  static maxInMatrix(m) {
    return math.max(m)
  }

  static makeMatrixOfValue(rows, cols, val) {
    let m = math.zeros(rows, cols)
    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < cols; j++) {
        m.set([i, j], val)
      }
    }
    return m
  }

  static createMatrixFromRow(rowArr, numRows) {
    let m = math.zeros(numRows, rowArr.length)
    for (let i = 0; i < numRows; i++) {
      for (let j = 0; j < rowArr.length; j++) {
        m.set([i, j], rowArr[j])
      }
    }
    return m
  }

  static matrixClampToMin(m, minMatrix) {
    let clamped = math.matrix(math.zeros(m.size()))
    for (let i = 0; i < m.size()[0]; i++) {
      for (let j = 0; j < m.size()[1]; j++) {
        clamped.set([i, j], clampToMin(m.get([i, j]), minMatrix.get([i, j])))
      }
    }
    return clamped
  }

  static writeToJson(m) {
    return m.toArray();
  }

  static readFromJson(json) {
    return matrix(json);
  }
};

export function scalarSum(arr) {
  let sum = 0
  for (let val of arr) {
    sum += val
  }
  return sum
}

export function makeVector(n) {
  return new Array(n).fill(0)
}

export function makeMonthVector(monthIndex) {
  let arr = new Array(12).fill(0)
  arr[monthIndex] = 1
  return arr
}

export function makeHourVector(hourIndex) {
  let arr = new Array(24).fill(0)
  arr[hourIndex] = 1
  return arr
}

// Make a true mod (unlike JS % operator, which handles -ves weird
// because it is a remainder operator, not a mod operator)
export function positiveModulo(n, m) {
  return ((n % m) + m) % m;
}

export function clampAngleZeroToTwoPi(angleRads) {
  return positiveModulo(angleRads, 2.0*PI);
}

export function clampAngleNegativePiToPi(angleRads) {
  // Angle may be in range [-2pi, 2pi]
  // Clamp angle to [-pi, pi]
  if (angleRads < -PI) {
    angleRads += 2.0*PI;
  } else if (angleRads > PI) {
    angleRads -= 2.0*PI;
  }
  return angleRads;
}

export function maxInArray(arr) {
  let max = arr[0]
  for (let i = 1; i < arr.length; i++) {
    if (arr[i] > max) {
      max = arr[i]
    }
  }
  return max
}

export class Rect {
  constructor(x, y, w, h) {
    this.x = x
    this.y = y
    this.w = w
    this.h = h
  }

  static createFromPoints(x1, y1, x2, y2) {
    return new Rect(x1, y1, x2 - x1, y2 - y1)
  }

  static empty() {
    return new Rect(0, 0, 0, 0)
  }

  isEmpty() {
    return this.w <= 0 || this.h <= 0
  }

  getArea() {
    return this.w * this.h;
  }

  setX1X2(x1, x2) {
    this.x = x1
    this.w = x2 - x1
  }

  setY1Y2(y1, y2) {
    this.y = y1
    this.h = y2 - y1
  }

  static intersect(r1, r2) {
    let x1 = Math.max(r1.x, r2.x)
    let y1 = Math.max(r1.y, r2.y)
    let x2 = Math.min(r1.x + r1.w, r2.x + r2.w)
    let y2 = Math.min(r1.y + r1.h, r2.y + r2.h)
    return new Rect(x1, y1, x2 - x1, y2 - y1)
  }

  static intersectOfRects(rects) {
    let r = rects[0]
    for (let i = 1; i < rects.length; i++) {
      r = Rect.intersect(r, rects[i])
    }
    return r
  }
};

export function calculateTangentAndNormal(data, pointIndex, xScale, yScale) {
  // Calculate tangent vector at this point
  let tangentX = 0
  let tangentY = 0
  
  if (pointIndex > 0 && pointIndex < data.length - 1) {
    // Use points before and after for better tangent calculation
    const prevPoint = data[pointIndex - 1]
    const nextPoint = data[pointIndex + 1]
    
    const prevXPixel = xScale.getPixelForValue(prevPoint.x)
    const prevYPixel = yScale.getPixelForValue(prevPoint.y)
    const nextXPixel = xScale.getPixelForValue(nextPoint.x)
    const nextYPixel = yScale.getPixelForValue(nextPoint.y)
    
    tangentX = nextXPixel - prevXPixel
    tangentY = nextYPixel - prevYPixel
  } else if (pointIndex > 0) {
    // Use previous point
    const prevPoint = data[pointIndex - 1]
    const point = data[pointIndex]
    const prevXPixel = xScale.getPixelForValue(prevPoint.x)
    const prevYPixel = yScale.getPixelForValue(prevPoint.y)
    const xPixel = xScale.getPixelForValue(point.x)
    const yPixel = yScale.getPixelForValue(point.y)
    
    tangentX = xPixel - prevXPixel
    tangentY = yPixel - prevYPixel
  } else if (pointIndex < data.length - 1) {
    // Use next point
    const point = data[pointIndex]
    const nextPoint = data[pointIndex + 1]
    const xPixel = xScale.getPixelForValue(point.x)
    const yPixel = yScale.getPixelForValue(point.y)
    const nextXPixel = xScale.getPixelForValue(nextPoint.x)
    const nextYPixel = yScale.getPixelForValue(nextPoint.y)
    
    tangentX = nextXPixel - xPixel
    tangentY = nextYPixel - yPixel
  }
  
  // Normalize the tangent vector
  const tangentLength = Math.sqrt(tangentX * tangentX + tangentY * tangentY)
  if (tangentLength > 0) {
    tangentX /= tangentLength
    tangentY /= tangentLength
  }
  
  // Calculate perpendicular (normal) vector to the tangent
  // Rotate tangent 90 degrees counterclockwise: (x, y) -> (-y, x)
  const normalX = -tangentY
  const normalY = tangentX
  
  return { tangentX, tangentY, normalX, normalY }
}